import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
import { mytToday, rm } from './penalties.js';

/**
 * 397 (#1328, mock-up "Penalty dashboard"): the period's penalties, totals and per car and per supplier. Counted by the
 * penalty's own day (as the sales statement), active penalties only; every figure is the server's. Each amount opens
 * the Penalties page with exactly the filters behind it, so the list there adds up to the same figure.
 * 409 (#1471, mock-up frame 5): paid out and to pay out are the suppliers' share; CARii's commission is its own tile
 * and column (no list filter matches it, so it is not a link).
 */
interface Figures {
  count: number;
  total_minor: number;
  collected_minor: number;
  unpaid_minor: number;
  paid_out_minor: number;
  to_pay_out_minor: number;
  commission_minor: number;
}
interface CarRow extends Figures {
  car_id: string;
  plate_number: string;
  car_label: string;
  supplier_id: string;
  supplier_name: string;
}
interface SupplierRow extends Figures {
  supplier_id: string;
  supplier_name: string;
}
export interface PenaltyDashboard {
  period: { from: string; to: string };
  supplier_id: string | null;
  totals: Figures;
  by_car: CarRow[];
  by_supplier: SupplierRow[];
}

const plural = (n: number) => `${n} ${n === 1 ? 'penalty' : 'penalties'}`;
const TH = 'px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-steel';
const THL = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-steel';
const TD = 'px-3 py-2 text-right text-sm whitespace-nowrap tabular-nums';
const TDL = 'px-3 py-2 text-left text-sm';

type Which = 'total' | 'collected' | 'unpaid' | 'paidout' | 'topay';
const WHICH_FILTER: Record<Which, Record<string, string>> = {
  total: {},
  collected: { paid: 'paid' },
  unpaid: { paid: 'unpaid' },
  paidout: { payout: 'done' },
  topay: { payout: 'pending' },
};

export function PenaltyDashboardPage() {
  const [from, setFrom] = useState(() => `${mytToday().slice(0, 7)}-01`);
  const [to, setTo] = useState(() => mytToday());
  const [supplier, setSupplier] = useState('');
  // The supplier choices are the suppliers seen in the answers (a role managing penalties need not read suppliers).
  const [suppliers, setSuppliers] = useState<Record<string, string>>({});
  const reversed = Boolean(from && to && to < from);
  const ask = new URLSearchParams({ from, to });
  if (supplier) ask.set('supplier_id', supplier);
  const url = `/penalties/dashboard?${ask.toString()}`;
  const q = useQuery({
    queryKey: ['penalties-dashboard', url],
    queryFn: () => api.get(url) as Promise<PenaltyDashboard>,
    enabled: Boolean(from && to) && !reversed,
  });
  const data = reversed ? undefined : q.data;
  useEffect(() => {
    const rows = q.data?.by_supplier ?? [];
    if (rows.length === 0) return;
    setSuppliers((s) => ({
      ...s,
      ...Object.fromEntries(rows.map((r) => [r.supplier_id, r.supplier_name])),
    }));
  }, [q.data]);

  /** The Penalties page, filtered to exactly what a figure counts. */
  const listHref = (which: Which, extra: Record<string, string> = {}) => {
    const p = new URLSearchParams({ from, to, status: 'active' });
    if (supplier) p.set('supplier_id', supplier);
    for (const [k, v] of Object.entries({ ...extra, ...WHICH_FILTER[which] })) p.set(k, v);
    return `/penalties?${p.toString()}`;
  };
  const amount = (testId: string, minor: number, href: string, tone = 'text-brand-blue') => (
    <Link data-testid={testId} to={href} className={`${tone} hover:underline`}>
      {rm(minor)}
    </Link>
  );
  const t = data?.totals;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <PageHeader
        title="Penalty dashboard"
        description="Penalties by their own day, as the Sales Statement counts them. Active penalties only."
      />
      <div className="flex flex-wrap items-end gap-3">
        <FormField label="From" name="pdash-from">
          <Input
            id="pdash-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormField>
        <FormField label="To" name="pdash-to">
          <Input id="pdash-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FormField>
        <FormField label="Supplier" name="pdash-supplier">
          <Select
            id="pdash-supplier"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
          >
            <option value="">All suppliers</option>
            {Object.entries(suppliers)
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([id, name]) => (
                <option key={id} value={id}>
                  {name || id.slice(-8)}
                </option>
              ))}
          </Select>
        </FormField>
        <span className="ml-auto pb-2 text-xs text-steel">
          By the penalty's day · Malaysia time
        </span>
      </div>
      {reversed && (
        <p
          role="alert"
          className="rounded-md border border-semantic-warning-text bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
        >
          “To” must be on or after “From”.
        </p>
      )}
      {!reversed && q.isLoading && <Spinner />}
      {!reversed && q.isError && (
        <p role="alert" className="text-sm text-semantic-danger-text">
          The penalty figures could not be loaded.
        </p>
      )}
      {t && data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Card padding="sm" data-testid="pen-dash-total">
              <p className="text-xs font-semibold text-steel">Total penalties</p>
              <p className="mt-1 text-2xl font-bold">
                {amount('pen-dash-total-link', t.total_minor, listHref('total'))}
              </p>
              <p className="text-xs text-steel">{plural(t.count)}</p>
            </Card>
            <Card padding="sm" data-testid="pen-dash-collected">
              <p className="text-xs font-semibold text-steel">Collected</p>
              <p className="mt-1 text-2xl font-bold">
                {amount(
                  'pen-dash-collected-link',
                  t.collected_minor,
                  listHref('collected'),
                  'text-semantic-success-text',
                )}
              </p>
              <p className="text-xs text-steel">paid by the customer</p>
            </Card>
            <Card padding="sm" data-testid="pen-dash-unpaid">
              <p className="text-xs font-semibold text-steel">Unpaid</p>
              <p className="mt-1 text-2xl font-bold">
                {amount(
                  'pen-dash-unpaid-link',
                  t.unpaid_minor,
                  listHref('unpaid'),
                  'text-semantic-danger-text',
                )}
              </p>
              <p className="text-xs text-steel">customers still owe</p>
            </Card>
            <Card padding="sm" data-testid="pen-dash-commission">
              <p className="text-xs font-semibold text-steel">CARii commission</p>
              <p className="mt-1 text-2xl font-bold">{rm(t.commission_minor)}</p>
              <p className="text-xs text-steel">CARii's share of the fines</p>
            </Card>
            <Card padding="sm" data-testid="pen-dash-paidout">
              <p className="text-xs font-semibold text-steel">Paid out to suppliers</p>
              <p className="mt-1 text-2xl font-bold">
                {amount('pen-dash-paidout-link', t.paid_out_minor, listHref('paidout'))}
              </p>
              <p className="text-xs text-steel">
                {'to pay out '}
                {amount('pen-dash-topay-link', t.to_pay_out_minor, listHref('topay'))}
              </p>
            </Card>
          </div>
          {data.by_car.length === 0 ? (
            <Card>
              <p className="text-sm text-steel">No penalties in this period.</p>
            </Card>
          ) : (
            <>
              <Card>
                <h2 className="mb-2 text-base font-bold">By car</h2>
                <div className="overflow-x-auto">
                  <table aria-label="Penalties by car" className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-hairline">
                        <th className={THL}>Car</th>
                        <th className={THL}>Supplier</th>
                        <th className={TH}>Penalties</th>
                        <th className={TH}>Total</th>
                        <th className={TH}>Collected</th>
                        <th className={TH}>Unpaid</th>
                        <th className={TH}>Commission</th>
                        <th className={TH}>Paid out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_car.map((c) => {
                        const id = `pen-dash-car-${c.car_id}`;
                        const extra = { car_id: c.car_id };
                        return (
                          <tr key={c.car_id} className="border-b border-hairline-soft">
                            <td className={TDL}>
                              <span className="font-semibold">{c.plate_number}</span>{' '}
                              <span className="text-steel">{c.car_label}</span>
                            </td>
                            <td className={TDL}>{c.supplier_name}</td>
                            <td className={TD}>{c.count}</td>
                            <td className={TD}>
                              {amount(`${id}-total`, c.total_minor, listHref('total', extra))}
                            </td>
                            <td className={TD}>
                              {amount(
                                `${id}-collected`,
                                c.collected_minor,
                                listHref('collected', extra),
                              )}
                            </td>
                            <td className={TD}>
                              {amount(
                                `${id}-unpaid`,
                                c.unpaid_minor,
                                listHref('unpaid', extra),
                                c.unpaid_minor > 0
                                  ? 'text-semantic-danger-text'
                                  : 'text-brand-blue',
                              )}
                            </td>
                            <td className={TD}>{rm(c.commission_minor)}</td>
                            <td className={TD}>
                              {amount(
                                `${id}-paidout`,
                                c.paid_out_minor,
                                listHref('paidout', extra),
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      <TotalsRow t={t} span={2} />
                    </tbody>
                  </table>
                </div>
              </Card>
              <Card>
                <h2 className="mb-2 text-base font-bold">By supplier</h2>
                <div className="overflow-x-auto">
                  <table aria-label="Penalties by supplier" className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-hairline">
                        <th className={THL}>Supplier</th>
                        <th className={TH}>Penalties</th>
                        <th className={TH}>Total</th>
                        <th className={TH}>Collected</th>
                        <th className={TH}>Unpaid</th>
                        <th className={TH}>Commission</th>
                        <th className={TH}>Paid out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_supplier.map((s) => {
                        const id = `pen-dash-supplier-${s.supplier_id}`;
                        const extra = { supplier_id: s.supplier_id };
                        return (
                          <tr key={s.supplier_id} className="border-b border-hairline-soft">
                            <td className={TDL}>{s.supplier_name}</td>
                            <td className={TD}>{s.count}</td>
                            <td className={TD}>
                              {amount(`${id}-total`, s.total_minor, listHref('total', extra))}
                            </td>
                            <td className={TD}>
                              {amount(
                                `${id}-collected`,
                                s.collected_minor,
                                listHref('collected', extra),
                              )}
                            </td>
                            <td className={TD}>
                              {amount(
                                `${id}-unpaid`,
                                s.unpaid_minor,
                                listHref('unpaid', extra),
                                s.unpaid_minor > 0
                                  ? 'text-semantic-danger-text'
                                  : 'text-brand-blue',
                              )}
                            </td>
                            <td className={TD}>{rm(s.commission_minor)}</td>
                            <td className={TD}>
                              {amount(
                                `${id}-paidout`,
                                s.paid_out_minor,
                                listHref('paidout', extra),
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      <TotalsRow t={t} span={1} />
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}

function TotalsRow({ t, span }: { t: Figures; span: number }) {
  return (
    <tr className="border-t-2 border-hairline font-bold">
      <td className={TDL} colSpan={span}>
        Total
      </td>
      <td className={TD}>{t.count}</td>
      <td className={TD}>{rm(t.total_minor)}</td>
      <td className={TD}>{rm(t.collected_minor)}</td>
      <td className={TD}>{rm(t.unpaid_minor)}</td>
      <td className={TD}>{rm(t.commission_minor)}</td>
      <td className={TD}>{rm(t.paid_out_minor)}</td>
    </tr>
  );
}
